import React, { useState } from 'react';
import { Building2, Factory, Car, Lightbulb, Ship, HardHat, Wrench, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const ApplicazioneTab = ({ isActive, icon: Icon, settore, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-4 py-3 transition-all whitespace-nowrap ${
        isActive 
          ? 'text-blue-600 font-medium border-b-2 border-blue-600' 
          : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300'
      }`}
    >
      <Icon size={20} className={`mr-2 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
      <span className={isActive ? 'font-medium' : ''}>{settore}</span>
    </button>
  );
};

// Componente dropdown per mobile
const MobileSelector = ({ activeTab, settore, applicazioniList, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative mb-6">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-left"
      >
        <div className="flex items-center">
          {React.createElement(applicazioniList[activeTab].icon, { 
            size: 20, 
            className: "text-blue-600 mr-2" 
          })}
          <span className="font-medium text-gray-800">{settore}</span>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-gray-600 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>
      
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {applicazioniList.map((app, idx) => (
            <button
              key={idx}
              onClick={() => {
                onChange(idx);
                setIsOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 ${
                idx === activeTab ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
              } ${idx !== applicazioniList.length - 1 ? 'border-b border-gray-200' : ''}`}
            >
              {React.createElement(app.icon, { 
                size: 20, 
                className: `mr-2 ${idx === activeTab ? 'text-blue-600' : 'text-gray-500'}` 
              })}
              <span>{app.settore}</span>
            </button>
          ))}
        </div>
      )}
      
      {isOpen && (
        <div 
          className="fixed inset-0 z-10 bg-transparent"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

const ApplicazioneDettaglio = ({ settore, descrizione, specifiche, immagine }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center py-6">
      {/* Immagine - su mobile viene visualizzata prima */}
      <div className="relative aspect-square md:aspect-auto md:h-full rounded-lg overflow-hidden shadow-lg transform transition-transform duration-500 hover:scale-105">
        <img
          src={immagine}
          alt={settore}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-80"></div>
        <div className="absolute bottom-0 left-0 w-full p-4">
          <h3 className="text-2xl font-bold text-white">{settore}</h3>
        </div>
      </div>

      {/* Contenuto */}
      <div className="space-y-6">
        <p className="text-lg text-gray-600 mb-4 leading-relaxed">{descrizione}</p>
        
        {specifiche && (
          <div className="bg-gray-50 border-l-4 border-blue-500 rounded-lg p-5 shadow-md">
            <h4 className="font-semibold text-gray-800 mb-4 text-lg">Specifiche tecniche</h4>
            <div className="space-y-4">
              {specifiche.map((spec, index) => (
                <div key={index} className="flex flex-col">
                  {spec.titolo && (
                    <div className="font-medium text-blue-700 text-sm tracking-wide mb-1">{spec.titolo}</div>
                  )}
                  <div className="text-gray-700">{spec.testo}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TabNavigation = ({ activeTab, visibleTabs, applicazioniList, onTabClick, onPrevClick, onNextClick }) => {
  return (
    <div className="max-w-6xl mx-auto mb-10 bg-white rounded-t-lg shadow-sm relative border-b border-gray-200 hidden md:flex">
      <div className="flex items-center w-full">
        <button 
          onClick={onPrevClick}
          className={`p-3 flex-shrink-0 focus:outline-none ${visibleTabs.start <= 0 ? 'text-gray-300 cursor-default' : 'text-gray-600 hover:text-blue-600'}`}
          disabled={visibleTabs.start <= 0}
        >
          <ChevronLeft size={24} />
        </button>
        
        <div className="flex-grow overflow-hidden">
          <div className="flex justify-between">
            {applicazioniList.slice(visibleTabs.start, visibleTabs.end + 1).map((app, index) => (
              <ApplicazioneTab
                key={visibleTabs.start + index}
                isActive={activeTab === visibleTabs.start + index}
                icon={app.icon}
                settore={app.settore}
                onClick={() => onTabClick(visibleTabs.start + index)}
              />
            ))}
          </div>
        </div>
        
        <button 
          onClick={onNextClick}
          className={`p-3 flex-shrink-0 focus:outline-none ${visibleTabs.end >= applicazioniList.length - 1 ? 'text-gray-300 cursor-default' : 'text-gray-600 hover:text-blue-600'}`}
          disabled={visibleTabs.end >= applicazioniList.length - 1}
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
};

const Applicazioni = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [visibleTabs, setVisibleTabs] = useState({ start: 0, end: 4 });

  const applicazioniList = [
    {
      icon: Wrench,
      settore: "Settore Siderurgico",
      descrizione: "Ritiriamo il vecchio e forniamo il nuovo. La nostra specializzazione nel settore siderurgico ci permette di offrire un servizio completo che va dalla raccolta dei materiali metallici usati alla fornitura di nuovi prodotti di alta qualità.",
      immagine: "/images/applicazioni_7.webp",
      specifiche: [
        {
          titolo: "PRODOTTI DISPONIBILI",
          testo: "Fornitura di materiale ossitagliato da lamiera da treno quarto con taglio fiamma/pantografo - Taglio al plasma HyDefinition"
        },
        {
          titolo: "NORMATIVE E STANDARD",
          testo: "Commercializzazione lamiere in fogli: EN 10025 -10029 – DIN 17100 - S235 S275 S355 grado JR-JO-J2 - C45"
        },
        {
          titolo: "DIMENSIONI",
          testo: "Spess. da 8mm a 300mm - Largh. da 1000mm a 3000mm - Lungh. fino a 12000mm"
        },
        {
          titolo: "CAPACITÀ",
          testo: "Peso singolo movimentabile max 10000 kg"
        },
        {
          titolo: "ATTREZZATURE",
          testo: "Disponiamo di: macchine da taglio con bancali di larghezza 3200-3200 per lunghezza 25 mt - Pressa per raddrizzare fino allo spessore 80mm"
        },
        {
          titolo: "TIPOLOGIE LAMIERE",
          testo: "Lamiera coil zincato EN 10346 DX51D, Lamiera coil EN10025-2/S355J2+N"
        }
      ]
    },
    {
      icon: Ship,
      settore: "Settore Nautico",
      descrizione: "Fornitura di materiali specifici per l'industria navale, con particolare attenzione ai legni pregiati e ai materiali resistenti all'ambiente marino. La nostra esperienza nel settore garantisce la massima qualità per applicazioni nautiche di ogni tipo.",
      immagine: "/images/applicazioni_5.webp",
      specifiche: [
        {
          titolo: "TEAK INDIA",
          testo: "Provenienza: Foresta naturale. Colore e caratteristiche: Legno marrone ed oleoso, venatura evidente. L'oleosità del legno lo rende immune all'attacco di agenti esterni e intrinsecamente resistente a coperta."
        },
        {
          titolo: "QUALITÀ DISPONIBILE",
          testo: "Quarter Sawn e FEQ, adatti per l'impiego nautico. FEQ minus, adatti per l'arredamento."
        },
        {
          titolo: "PRODOTTI",
          testo: "Tavolame - Doghe e Mastre."
        },
        {
          titolo: "FORMATO",
          testo: "Spessori: mm 27/40/52. Larghezze: mm 80-155+. Lunghezze: mm 900-1700, 1800-3000+."
        }
      ]
    },
    {
      icon: Building2,
      settore: "Edilizia",
      descrizione: "Forniamo materiali per costruzione e infrastrutture, da acciaio strutturale a soluzioni innovative per l'efficienza energetica. Progettiamo e realizziamo insieme ai clienti strutture sicure e sostenibili.",
      immagine: "/images/applicazioni_1.webp",
      specifiche: null
    },
    {
      icon: Factory,
      settore: "Industria Manifatturiera",
      descrizione: "Offriamo materie prime e soluzioni integrate per l'industria, supportando i processi produttivi e favorendo l'adozione di tecnologie avanzate per un'industria più sostenibile ed efficiente.",
      immagine: "/images/applicazioni_2.webp",
      specifiche: null
    },
    {
      icon: Car,
      settore: "Automotive",
      descrizione: "La nostra offerta comprende materiali ad alte prestazioni per l'industria automobilistica, inclusi acciai leggeri e ad alta resistenza, pensati per migliorare sicurezza, efficienza e prestazioni dei veicoli.",
      immagine: "/images/applicazioni_3.webp",
      specifiche: null
    },
    {
      icon: Lightbulb,
      settore: "Energia",
      descrizione: "Collaboriamo con il settore energetico, offrendo soluzioni per la costruzione di impianti energetici e infrastrutture, dalle fonti rinnovabili ai sistemi tradizionali, con un occhio alla sostenibilità.",
      immagine: "/images/applicazioni_4.webp",
      specifiche: null
    },
    {
      icon: HardHat,
      settore: "Ingegneria Civile",
      descrizione: "Offriamo soluzioni per la realizzazione di infrastrutture civili, inclusi ponti, gallerie e strade, garantendo materiali sicuri, durevoli e conformi agli standard più elevati di sicurezza.",
      immagine: "/images/applicazioni_6.webp",
      specifiche: null
    }
  ];

  const showNextTabs = () => {
    if (visibleTabs.end < applicazioniList.length - 1) {
      setVisibleTabs({
        start: visibleTabs.start + 1,
        end: Math.min(visibleTabs.end + 1, applicazioniList.length - 1)
      });
    }
  };

  const showPrevTabs = () => {
    if (visibleTabs.start > 0) {
      setVisibleTabs({
        start: visibleTabs.start - 1,
        end: visibleTabs.end - 1
      });
    }
  };

  return (
    <section className="py-12 md:py-20 px-4 md:px-6 bg-gray-100">
      {/* Titolo sezione */}
      <div className="text-center mb-8 md:mb-12">
        <h2 className="text-3xl md:text-4xl font-semibold text-gray-800 mb-3 md:mb-4">
          Settori di Applicazione
        </h2>
        <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
          Scopri come i nostri prodotti supportano e trasformano una varietà di settori industriali, offrendo soluzioni innovative per ogni esigenza.
        </p>
      </div>

      {/* Navigazione desktop con tabs */}
      <TabNavigation 
        activeTab={activeTab}
        visibleTabs={visibleTabs}
        applicazioniList={applicazioniList}
        onTabClick={setActiveTab}
        onPrevClick={showPrevTabs}
        onNextClick={showNextTabs}
      />

      {/* Selettore mobile (dropdown) - visibile solo su schermi piccoli */}
      <div className="md:hidden px-4 mb-4">
        <MobileSelector 
          activeTab={activeTab}
          settore={applicazioniList[activeTab].settore}
          applicazioniList={applicazioniList}
          onChange={setActiveTab}
        />
      </div>

      {/* Contenuto dell'applicazione attiva */}
      <div className="max-w-6xl mx-auto bg-white p-4 md:p-8 rounded-lg md:rounded-b-lg shadow-md">
        <div className="relative mb-2">
          <div className="absolute -top-8 md:-top-12 left-0 w-16 md:w-20 h-1 bg-blue-600"></div>
        </div>
        <ApplicazioneDettaglio 
          {...applicazioniList[activeTab]}
        />
      </div>

      {/* Navigazione mobile (sotto il contenuto) */}
      <div className="flex justify-center mt-8 md:hidden">
        <div className="flex gap-2">
          <button 
            onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
            className={`p-3 bg-white rounded-full shadow ${activeTab === 0 ? 'text-gray-300' : 'text-gray-600'}`}
            disabled={activeTab === 0}
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex items-center px-4 bg-white rounded-full text-sm font-medium">
            {activeTab + 1} / {applicazioniList.length}
          </div>
          <button 
            onClick={() => setActiveTab(prev => Math.min(applicazioniList.length - 1, prev + 1))}
            className={`p-3 bg-white rounded-full shadow ${activeTab === applicazioniList.length - 1 ? 'text-gray-300' : 'text-gray-600'}`}
            disabled={activeTab === applicazioniList.length - 1}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Applicazioni;